import { Bell } from 'lucide-react'
import { shellNavigate } from '../lib/navigation'
import { TMM } from '../lib/tmm'
import { useUnseenGreatLeads } from './great-leads'

// The great-lead bell (instance.leadAlerts): lit, with a count, while a great
// new lead is unseen; one tap opens the leads page on the great leads. The
// alert email links straight to the lead; the bell is the way back in from
// anywhere in the Command Centre (Sam, 12 Oct 2026).

export const GREAT_LEADS_PATH = '/website-leads?view=great'

const RED = TMM.red

function Badge({ n }: { n: number }) {
  return (
    <span
      className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10.5px] font-bold leading-none text-white"
      style={{ background: RED, boxShadow: '0 0 0 2px var(--color-surface-1, #fff)' }}
    >
      {n > 9 ? '9+' : n}
    </span>
  )
}

/** The glow behind a lit bell: a soft red ring that breathes. */
function Glow() {
  return <span aria-hidden className="absolute inset-0 animate-ping rounded-full opacity-40" style={{ background: RED }} />
}

export function GreatLeadBell({ variant, onNavigate }: { variant: 'panel' | 'rail' | 'phone'; onNavigate?: () => void }) {
  const unseen = useUnseenGreatLeads()
  const n = unseen.length
  const lit = n > 0
  const label = lit ? `${n} great new lead${n === 1 ? '' : 's'}` : 'Great leads: nothing new'
  const go = () => {
    onNavigate?.()
    shellNavigate(GREAT_LEADS_PATH)
  }

  if (variant === 'panel') {
    return (
      <button
        type="button"
        onClick={go}
        title={lit ? `Newest: ${unseen[0].name}` : undefined}
        className={`mb-2 flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors ${
          lit ? 'font-semibold text-white' : 'text-text-secondary hover:bg-surface-2 hover:text-text-primary'
        }`}
        style={lit ? { background: RED, boxShadow: `0 0 14px ${RED}66` } : undefined}
      >
        <span className="relative flex h-4 w-4 shrink-0 items-center justify-center">
          {lit && <span aria-hidden className="absolute inset-[-4px] animate-ping rounded-full bg-white opacity-30" />}
          <Bell className="relative h-4 w-4" />
        </span>
        <span>{lit ? label : 'Great leads'}</span>
      </button>
    )
  }

  const size = variant === 'phone' ? 'h-10 w-10' : 'mb-2 h-9 w-9'
  return (
    <button
      type="button"
      onClick={go}
      title={label}
      aria-label={label}
      className={`relative flex ${size} shrink-0 items-center justify-center rounded-full transition-colors hover:bg-surface-2 ${
        lit ? '' : 'text-text-muted hover:text-text-primary'
      }`}
      style={lit ? { color: RED } : undefined}
    >
      {lit && <Glow />}
      <Bell className={variant === 'phone' ? 'relative h-5 w-5' : 'relative h-4 w-4'} />
      {lit && <Badge n={n} />}
    </button>
  )
}
